import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import MainCourses from './components/MainCourses';
import { useTheme } from 'next-themes';
import { Navbar, NavbarContent, NavbarMenuToggle, NavbarBrand, NavbarItem, NavbarMenu, NavbarMenuItem, Dropdown, DropdownTrigger, Button, DropdownMenu, DropdownItem } from '@nextui-org/react';
import { ReactComponent as MobileSP } from './images/mobileSP.svg'
import DarkMode from './components/DarkMode';
import About from './components/About';
import ContactMe from './components/ContactMe';
import { ReactComponent as MobileDarkSP } from './images/mobileDarkSP.svg'
import Schedule from './components/Schedule';
import Live from "./lottie/live.json";
import LandingPage from './components/LandingPage';
import { ChevronDown } from './components/Icons';
import BSP from './components/BSP';
import SrilaPrabhupada from './components/SrilaPrabhupada';
import Lottie from 'lottie-react';
import AnimatedTextCharacter from './components/AnimatedTextCharacter';
import PrivacyPolicy from './components/PrivacyPolicy';
import FullHeightAccordions from './components/FullHeightAccordions';
import { AuthProvider } from './context/AuthContext';
import { triggerEpicConfetti } from './utils/confetti';
import { Toaster } from 'react-hot-toast';
import AuthModal from './components/AuthModal';
import AuthButton from './components/AuthButton';
import MyNotes from './components/MyNotes';
import { useAuth } from './hooks/useAuth';
import { trackPageView } from './services/analytics';
import ThematicStudiesLayout from './features/thematic-studies/components/ThematicStudiesLayout';
import ThematicStudiesIndexPage from './features/thematic-studies/pages/ThematicStudiesIndexPage';
import ThemeTopicsPage from './features/thematic-studies/pages/ThemeTopicsPage';
import TopicPage from './features/thematic-studies/pages/TopicPage';

function App() {
    const { setTheme } = useTheme();

    useEffect(() => {
        setTheme('light')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthProvider>
            <Toaster
                position='top-center'
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        style: {
                            background: '#4ade80',
                            color: '#000',
                        },
                    },
                    error: {
                        style: {
                            background: '#ef4444',
                            color: '#fff',
                        },
                    },
                }}
            />
            <AuthModal />
            <AppContent />
        </AuthProvider>
    );
}

function AppContent() {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const { pathname } = useLocation();
    const { user, showAuthModal } = useAuth();
    const areTermsAccepted = sessionStorage.getItem("accepted");
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [previousAuthModalState, setPreviousAuthModalState] = React.useState(showAuthModal);

    // One Analytics page view per route (the app changes pages without reloading)
    useEffect(() => {
        trackPageView(pathname);
    }, [pathname]);

    useEffect(() => {
        setIsMenuOpen(false)
    }, [pathname]);

    // Trigger confetti when authentication succeeds
    useEffect(() => {
        // Check if modal was open and is now closed, and user is authenticated
        if (previousAuthModalState && !showAuthModal && user) {
            // Add 200ms delay to ensure modal close animation completes
            setTimeout(() => {
                triggerEpicConfetti(3000);
            }, 200);
        }
        setPreviousAuthModalState(showAuthModal);
    }, [showAuthModal, user, previousAuthModalState]);

    const icons = {
        // @ts-ignore
        chevron: <ChevronDown fill='currentColor' size={16} />,
    };
    const SPLink = '/srilaprabhupada'
    const ISKCONLink = '/bsp'

    const menuItems: Array<{
        title: string;
        link: string;
        isActive: boolean;
        requiresAuth?: boolean;
    }> = [
            {
                title: 'Courses',
                link: '/courses',
                isActive: pathname.includes('courses')
            },
            {
                title: 'About',
                link: '/about',
                isActive: pathname.includes('about')
            },
            {
                title: 'Zoom Sessions',
                link: '/sessions',
                isActive: pathname.includes('sessions')
            },
            {
                title: 'Thematic Library',
                link: '/library',
                isActive: pathname.includes('library')
            },
            {
                title: 'Thematic Studies',
                link: '/thematic-studies',
                isActive: pathname.includes('thematic-studies')
            },
            {
                title: 'Ask Questions',
                link: '/contact',
                isActive: pathname.includes('contact')
            },
            {
                title: 'My Notes',
                link: '/my-notes',
                isActive: pathname.includes('my-notes'),
                requiresAuth: true
            },
        ]

    useEffect(() => {
        if (areTermsAccepted !== 'Yes') {
            navigate(`/?redirectTo=${pathname}`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areTermsAccepted]);

    // const text = "Bhakti Grantha Sevā".split(" ");
    const text = "Bhakti Grantha Sevā";

    return (
        <div className={'bg-white dark:bg-black/10 min-h-screen '}>
            {areTermsAccepted === 'Yes' && <Navbar isMenuOpen={isMenuOpen}
                onMenuOpenChange={setIsMenuOpen}
                isBordered
                maxWidth={'2xl'}
                position={'sticky'}
                className='z-[40]'
                classNames={{
                    wrapper: 'z-[40]',
                    menu: 'z-[41]',
                    content: 'z-[41]',
                    base: 'z-[40]',
                    item: [
                        "flex",
                        "relative",
                        "h-full",
                        "items-center",
                        "text-darkBlue",
                        "dark:text-lightGrey",
                        "data-[active=true]:text-lightOrange",
                        "dark:data-[active=true]:text-lightBlue",
                        "data-[active=true]:font-bold",
                    ],
                    menuItem: [
                        "text-darkBlue",
                        "dark:text-lightGrey",
                        "data-[active=true]:text-lightOrange",
                        "dark:data-[active=true]:text-lightBlue",
                        "data-[active=true]:font-bold",
                    ],

                }}>
                <NavbarContent data-justify={'end'}>
                    <NavbarMenuToggle
                        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                        className='xl:hidden' />
                    <NavbarBrand>
                        <div onClick={() => navigate('/')} className={'cursor-pointer'}>
                            {theme === 'light' ? <MobileSP /> : <MobileDarkSP />}
                        </div>
                        <Link className='ml-3 font-extrabold sm:text-4xl text-2xl text-darkBlue dark:text-lightGrey' to={'/'}>
                            <AnimatedTextCharacter text={text} />
                        </Link>
                    </NavbarBrand>
                </NavbarContent>

                <NavbarContent className='hidden xl:flex gap-4' justify={'end'}>
                    {menuItems
                        .filter(item => !item.requiresAuth || user)
                        .map((item, index) => {
                            if (index !== 1) {
                                return (
                                    <NavbarItem key={`${item.title}-${index}`} isActive={item.isActive}>
                                        <Link
                                            className={'font-bold text-lg'}
                                            to={item.link}
                                        >
                                            {item.title}
                                            {item.title.includes('Zoom Sessions') &&
                                                <div className={'w-7 absolute right-[-8px] top-[11px]'}>
                                                    <Lottie
                                                        animationData={Live}
                                                        loop={true}
                                                        autoplay={true}
                                                        style={{ width: '100%', height: '100%' }}
                                                    />
                                                </div>}

                                        </Link>
                                    </NavbarItem>
                                )
                            }
                            return (
                                <Dropdown>
                                    <NavbarItem>
                                        <DropdownTrigger>
                                            <Button
                                                disableRipple
                                                className={`font-bold text-lg ${(pathname.includes(SPLink) || pathname.includes(ISKCONLink)) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                                endContent={icons.chevron}
                                                radius='sm'
                                                variant='light'
                                            >
                                                About
                                            </Button>
                                        </DropdownTrigger>
                                    </NavbarItem>
                                    <DropdownMenu
                                        aria-label='ACME features'
                                        className='w-[340px]'
                                        itemClasses={{
                                            base: "gap-4",
                                        }}
                                    >
                                        <DropdownItem
                                            key='Srila Prabhupāda'
                                            className={`font-bold text-lg ${(pathname.includes(SPLink)) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                            onClick={() => navigate(SPLink)}
                                        >
                                            <Link
                                                className={'font-bold text-lg'}
                                                to={SPLink}
                                            >
                                                Śrīla Prabhupāda & Education
                                            </Link>
                                        </DropdownItem>
                                        <DropdownItem
                                            key='BSP'
                                            className={`font-bold text-lg ${(pathname.includes(ISKCONLink)) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                            onClick={() => navigate(ISKCONLink)}

                                        >
                                            <Link
                                                className={'font-bold text-lg'}
                                                to={ISKCONLink}
                                            >
                                                Balarāma Śakti Dāsa
                                            </Link>
                                        </DropdownItem>
                                        <DropdownItem
                                            key='privacy'
                                            className={`font-bold text-lg ${(pathname.includes('/privacy-policy')) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                            onClick={() => navigate('/privacy-policy')}

                                        >
                                            <Link
                                                className={'font-bold text-lg'}
                                                to={'/privacy-policy'}
                                            >
                                                Copyright Notice & Request
                                            </Link>
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            )
                        })}
                    <NavbarItem>
                        <DarkMode />
                    </NavbarItem>
                    <NavbarItem>
                        <AuthButton />
                    </NavbarItem>
                </NavbarContent>
                <NavbarContent justify={'end'} className={'lg:hidden'}>
                    <NavbarItem>
                        <DarkMode />
                    </NavbarItem>
                    <NavbarItem>
                        <AuthButton />
                    </NavbarItem>
                </NavbarContent>
                <NavbarMenu>
                    {menuItems
                        .filter(item => !item.requiresAuth || user)
                        .map((item, index) => {
                            if (index !== 1) {
                                return (
                                    <NavbarMenuItem key={`${item.title}-${index}`} isActive={item.isActive}>
                                        <Link
                                            className='w-full'
                                            to={item.link}
                                        >
                                            {item.title}
                                        </Link>
                                    </NavbarMenuItem>
                                )
                            }
                            return (
                                <Dropdown>
                                    <NavbarMenuItem>
                                        <DropdownTrigger>
                                            <Button
                                                disableRipple
                                                className={`font-bold text-lg ${(pathname.includes(SPLink) || pathname.includes(ISKCONLink)) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                                endContent={icons.chevron}
                                                radius='sm'
                                                variant='light'
                                            >
                                                About
                                            </Button>
                                        </DropdownTrigger>
                                    </NavbarMenuItem>
                                    <DropdownMenu
                                        aria-label='ACME features'
                                        className='w-[340px]'
                                        itemClasses={{
                                            base: "gap-4",
                                        }}
                                    >
                                        <DropdownItem
                                            key='Srila Prabhupāda'
                                            className={`font-bold text-lg ${pathname.includes(SPLink) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                            onClick={() => navigate(SPLink)}
                                        >
                                            <Link
                                                className={'text-lg'}
                                                to={SPLink}
                                            >
                                                Śrīla Prabhupāda & Education
                                            </Link>
                                        </DropdownItem>
                                        <DropdownItem
                                            key='BSP'
                                            className={`font-bold text-lg ${(pathname.includes(ISKCONLink)) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                            onClick={() => navigate(ISKCONLink)}

                                        >
                                            <Link
                                                className={'text-lg'}
                                                to={ISKCONLink}
                                            >
                                                Balarāma Śakti Dāsa
                                            </Link>
                                        </DropdownItem>
                                        <DropdownItem
                                            key='privacy'
                                            className={`font-bold text-lg ${(pathname.includes('/privacy-policy')) ? 'text-lightOrange' : 'text-darkBlue'}`}
                                            onClick={() => navigate('/privacy-policy')}

                                        >
                                            <Link
                                                className={'text-lg'}
                                                to={'/privacy-policy'}
                                            >
                                                Copyright Notice & Request
                                            </Link>
                                        </DropdownItem>

                                    </DropdownMenu>
                                </Dropdown>
                            )

                        })}
                </NavbarMenu>
            </Navbar>}

            <Routes>
                <Route path='/courses/*' element={<MainCourses />} />
                <Route path='/about' element={<About />} />
                <Route path='/contact' element={<ContactMe />} />
                <Route path='/sessions' element={<Schedule />} />
                <Route path={ISKCONLink} element={<BSP />} />
                <Route path='/srilaprabhupada' element={<SrilaPrabhupada />} />
                <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                <Route path='/library' element={<FullHeightAccordions />} />
                <Route path='/thematic-studies' element={<ThematicStudiesLayout />}>
                    <Route index element={<ThematicStudiesIndexPage />} />
                    <Route path=':themeSlug' element={<ThemeTopicsPage />} />
                    <Route path=':themeSlug/:topicSlug' element={<TopicPage />} />
                </Route>
                <Route path='/my-notes' element={<MyNotes />} />
                <Route path='/' element={<LandingPage />} />
                <Route
                    path='/'
                    element={<Navigate to='/' replace />}
                />
            </Routes>
            <div className={'fixed left-0 bottom-0 w-full text-black text-center bg-white/50 z-40 backdrop-blur-lg'}>
                <p className={'sm:text-lg text-md'}>©️All rights reserved Bhakti Grantha Sevā, 2024</p>
            </div>
        </div>
    );
}

export default App;

